import { Box, Button, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BodyPartType } from '../../utils/visualUtils';
import {
  clearVisualAnalysisImages,
  getCurrentStep,
  saveVisualAnalysisImage,
} from '../../utils/visualUtils';
import CameraCapture from './CameraCapture';
import {
  landingCard,
  landingContainer,
  landingSubtitle,
  landingTitle,
  startButton,
} from './VisualHealthAnalysisStyle';
import React from 'react';
import TopBar from '../../components/TopBar';
import SettingsBars from '../../components/SettingsBars';
import { useCameras } from '../../hooks';
import styled from 'styled-components';
import media from '../../style/media';
import { Flex } from '../../components/shared/Flex';
type Step = 0 | 1 | 2 | 3 | 4;
const STEP_TO_BODY_PART: Record<number, BodyPartType> = {
  1: 'eyes',
  2: 'lips',
  3: 'tongue',
  4: 'nails',
};
/* ---------------------- PAGE LAYOUT WRAPPERS -------------------- */
const PageContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background: #F7F8FA;
  position: relative;
  overflow-x: hidden;
`;
const ContentWrapper = styled.div`
  width: 100%;
  padding: 2rem 1rem 2rem;
  margin: 0 auto;
  box-sizing: border-box;
  ${media.tablet`
    padding: 6rem 2rem 2rem;
  `}
`;
const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  padding: 2.5rem 2rem;
  width: 100%;
  box-sizing: border-box;
  ${media.tablet`
    padding: 2.5rem 2.5rem;
  `}
`;
/* ---------------------- MAIN COMPONENT -------------------- */
export default function VisualHealthAnalysis() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>(0);
  const cameras = useCameras();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const toggleSettings = () => setIsSettingsOpen((prev) => !prev);
  // Load last saved step if exists
  useEffect(() => {
    const savedStep = getCurrentStep();
    if (savedStep > 1 && savedStep <= 4) {
      setCurrentStep(savedStep as Step);
    }
  }, []);
  const handleStart = () => {
    clearVisualAnalysisImages();
    setCurrentStep(1);
  };
  const handleCapture = (base64Image: string) => {
    const bodyPart = STEP_TO_BODY_PART[currentStep];
    try {
      saveVisualAnalysisImage(bodyPart, base64Image);
      if (currentStep < 4) {
        setCurrentStep((prev) => (prev + 1) as Step);
      } else {
        navigate('/camera-scan', {
          state: { visualAnalysisComplete: true },
        });
      }
    } catch (error) {
      console.error('Failed to save image:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to save image. Please try again.'
      );
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    } else {
      setCurrentStep(0);
    }
  };
  /* ---------------------- LANDING PAGE -------------------- */
  if (currentStep === 0) {
    return (
      <PageContainer>
        <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
        <SettingsBars
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          cameras={cameras}
          isLicenseValid={true}
        />
        <ContentWrapper>
          <Card>
            <Box sx={landingContainer}>
              <Paper elevation={10} sx={landingCard}>
                <Typography variant="h4" sx={landingTitle}>
                  Visual Health Analysis in 4 easy steps
                </Typography>
                <Typography sx={landingSubtitle}>
                  Our AI will guide you through capturing four photos for a
                  visual health assessment.
                </Typography>
                <Button onClick={handleStart} sx={startButton}>
                  Start Now
                </Button>
              </Paper>
            </Box>
          </Card>
        </ContentWrapper>
      </PageContainer>
    );
  }
  /* ---------------------- CAMERA CAPTURE -------------------- */
  const bodyPart = STEP_TO_BODY_PART[currentStep];
  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        cameras={cameras}
        isLicenseValid={true}
      />
      <ContentWrapper>
        <Card>
          <CameraCapture
            type={bodyPart}
            stepNumber={currentStep}
            onCapture={handleCapture}
            onBack={handleBack}
            isLastStep={currentStep === 4}
          />
        </Card>
      </ContentWrapper>
    </PageContainer>
  );
}